/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Download, FileSpreadsheet } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type AnalyticsParams,
  type AnalyticsReport,
  type StaffType,
  analyticsApi,
} from '../../lib/api/analytics.js';
import { useAnalyticsReport } from '../../lib/queries/analytics.js';
import { formatInr } from '../../lib/utils/money.js';

type RangePreset = 'week' | 'month' | 'custom';
const STAFF_TYPES: readonly StaffType[] = ['all', 'on_roll', 'consultant'];

const SELECT =
  'focus-ring h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const CARD =
  'rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900';

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

/** Resolve the preset + custom inputs into concrete query params. */
function paramsFor(
  preset: RangePreset,
  from: string,
  to: string,
  staffType: StaffType,
): AnalyticsParams {
  const base = staffType === 'all' ? {} : { staffType };
  if (preset === 'week') {
    return { ...base, from: isoDaysAgo(6), to: isoDaysAgo(0) };
  }
  if (preset === 'month') {
    return { ...base, from: isoDaysAgo(29), to: isoDaysAgo(0) };
  }
  return { ...base, ...(from !== '' ? { from } : {}), ...(to !== '' ? { to } : {}) };
}

function KpiRow({ report }: { readonly report: AnalyticsReport }): JSX.Element {
  const { t } = useTranslation();
  const k = report.kpis;
  const cells: readonly (readonly [string, string])[] = [
    [t('analyticsReport.kpi.revenue'), formatInr(k.revenuePaise)],
    [t('analyticsReport.kpi.sessions'), String(k.sessions)],
    [t('analyticsReport.kpi.completed'), String(k.completed)],
    [
      t('analyticsReport.kpi.satisfaction'),
      k.avgSatisfactionPct === null ? '—' : `${String(k.avgSatisfactionPct)}%`,
    ],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cells.map(([label, value]) => (
        <div key={label} className={CARD}>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-2xl font-semibold text-slate-800 dark:text-white">{value}</p>
        </div>
      ))}
    </div>
  );
}

function TherapistTable({ report }: { readonly report: AnalyticsReport }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <tr>
            {['therapist', 'type', 'sessions', 'completed', 'revenue'].map((c) => (
              <th key={c} className="px-5 py-3 font-semibold">
                {t(`analyticsReport.col.${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {report.byTherapist.map((r) => (
            <tr key={r.id}>
              <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{r.name}</td>
              <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                {t(`analyticsReport.staff.${r.category}`)}
              </td>
              <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.sessions}</td>
              <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.completed}</td>
              <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                {formatInr(r.revenuePaise)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Breakdowns({ report }: { readonly report: AnalyticsReport }): JSX.Element {
  const { t } = useTranslation();
  const rt = report.revenueByType;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={CARD}>
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
          {t('analyticsReport.statusTitle')}
        </h3>
        <ul className="space-y-1.5 text-sm">
          {report.statusBreakdown.map((s) => (
            <li key={s.status} className="flex justify-between text-slate-600 dark:text-slate-300">
              <span>{s.status.replace(/_/g, ' ')}</span>
              <span className="font-medium tabular-nums">{s.count}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={CARD}>
        <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
          {t('analyticsReport.revenueByTypeTitle')}
        </h3>
        <ul className="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
          <li className="flex justify-between">
            <span>{t('analyticsReport.staff.on_roll')}</span>
            <span className="font-medium tabular-nums">{formatInr(rt.onRollPaise)}</span>
          </li>
          <li className="flex justify-between">
            <span>{t('analyticsReport.staff.consultant')}</span>
            <span className="font-medium tabular-nums">{formatInr(rt.consultantPaise)}</span>
          </li>
          <li className="flex justify-between">
            <span>{t('analyticsReport.unattributed')}</span>
            <span className="font-medium tabular-nums">{formatInr(rt.unattributedPaise)}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

/** CR-001 filtered analytics report + PDF/Excel export, layered under the Figma overview. */
export function AnalyticsReportSection(): JSX.Element {
  const { t } = useTranslation();
  const [preset, setPreset] = useState<RangePreset>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [staffType, setStaffType] = useState<StaffType>('all');
  const params = useMemo(
    () => paramsFor(preset, from, to, staffType),
    [preset, from, to, staffType],
  );
  const query = useAnalyticsReport(params);

  const onPreset = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPreset(e.target.value as RangePreset);
  }, []);
  const onStaff = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setStaffType(e.target.value as StaffType);
  }, []);
  const onFrom = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFrom(e.target.value);
  }, []);
  const onTo = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTo(e.target.value);
  }, []);
  const onPdf = useCallback(() => {
    void analyticsApi.downloadPdf(params);
  }, [params]);
  const onXlsx = useCallback(() => {
    void analyticsApi.downloadXlsx(params);
  }, [params]);

  return (
    <section className="mt-8 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
          {t('analyticsReport.title')}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t('analyticsReport.range')}
            value={preset}
            onChange={onPreset}
            className={SELECT}
          >
            <option value="week">{t('analyticsReport.presets.week')}</option>
            <option value="month">{t('analyticsReport.presets.month')}</option>
            <option value="custom">{t('analyticsReport.presets.custom')}</option>
          </select>
          {preset === 'custom' && (
            <>
              <input
                type="date"
                aria-label={t('analyticsReport.from')}
                value={from}
                onChange={onFrom}
                className={SELECT}
              />
              <input
                type="date"
                aria-label={t('analyticsReport.to')}
                value={to}
                onChange={onTo}
                className={SELECT}
              />
            </>
          )}
          <select
            aria-label={t('analyticsReport.staffType')}
            value={staffType}
            onChange={onStaff}
            className={SELECT}
          >
            {STAFF_TYPES.map((s) => (
              <option key={s} value={s}>
                {t(`analyticsReport.staff.${s}`)}
              </option>
            ))}
          </select>
          <Button variant="ghost" size="sm" onClick={onPdf}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('analyticsReport.exportPdf')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onXlsx}>
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            {t('analyticsReport.exportXlsx')}
          </Button>
        </div>
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={query.data !== undefined && query.data.kpis.sessions === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('analyticsReport.empty')}
      >
        {query.data !== undefined && (
          <div className="space-y-4">
            <KpiRow report={query.data} />
            <Breakdowns report={query.data} />
            <TherapistTable report={query.data} />
          </div>
        )}
      </QueryState>
    </section>
  );
}
